import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
} from '@mui/material';

export type FeedbackKind = 'MissingQuestion' | 'UnknownTerm';

interface AiFeedbackDialogProps {
    open: boolean;
    kind: FeedbackKind;
    /** Vorbelegung des Zusammenhangs — die letzten Gesprächsbeiträge. */
    defaultContext?: string;
    onClose: () => void;
    onSubmit: (data: { term: string; context: string; requirementId: string | null }) => Promise<void>;
}

/**
 * Rückmeldung an die Katalogpflege.
 *
 * Statt eines `window.prompt`: der Melder soll im Gespräch drei Sekunden brauchen, die
 * Katalogpflege danach aber genug in der Hand haben, um die Lücke wirklich zu schließen. Der
 * Zusammenhang ist deshalb vorbelegt und die Anforderungs-Id optional — wer sie schon kennt, spart
 * dem Nächsten das Suchen.
 */
export const AiFeedbackDialog: React.FC<AiFeedbackDialogProps> = ({
    open, kind, defaultContext, onClose, onSubmit,
}) => {
    const [term, setTerm] = useState('');
    const [context, setContext] = useState('');
    const [requirementId, setRequirementId] = useState('');
    const [busy, setBusy] = useState(false);

    // Beim Öffnen neu vorbelegen — der Dialog überlebt zwischen zwei Meldungen.
    useEffect(() => {
        if (!open) return;
        setTerm('');
        setRequirementId('');
        setContext(defaultContext ?? '');
    }, [open, defaultContext]);

    const isTerm = kind === 'UnknownTerm';

    const submit = async () => {
        if (!term.trim()) return;
        setBusy(true);
        try {
            await onSubmit({
                term: term.trim(),
                context: context.trim(),
                requirementId: requirementId.trim() || null,
            });
            onClose();
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isTerm ? 'Begriff nicht erkannt' : 'Frage hat gefehlt'}
            </DialogTitle>

            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    {isTerm
                        ? 'Welchen Begriff hat der Kunde benutzt, den der Assistent nicht zuordnen konnte?'
                        : 'Zu welchem Thema hätte der Assistent fragen sollen?'}
                </DialogContentText>

                <Stack spacing={2}>
                    <TextField
                        autoFocus fullWidth size="small"
                        label={isTerm ? 'Begriff des Kunden' : 'Thema'}
                        value={term}
                        onChange={(e) => setTerm(e.target.value)}
                        placeholder={isTerm ? 'z. B. Handfunkgerät' : 'z. B. Zellenruf'}
                        helperText={isTerm
                            ? 'Genau so, wie der Kunde es gesagt hat — daraus wird später ein Stichwort.'
                            : 'Der Bereich, zu dem eine Gesprächsfrage fehlt.'}
                    />

                    <TextField
                        fullWidth size="small" multiline minRows={2} maxRows={5}
                        label="Zusammenhang"
                        value={context}
                        onChange={(e) => setContext(e.target.value)}
                        helperText="Vorbelegt aus dem Gespräch. Kürze oder ergänze, was zum Verstehen nötig ist."
                    />

                    <TextField
                        fullWidth size="small"
                        label="Anforderung (optional)"
                        value={requirementId}
                        onChange={(e) => setRequirementId(e.target.value)}
                        placeholder="z. B. R.Fu.7"
                        helperText="Wenn du schon weißt, wohin es gehört — das spart der Pflege das Suchen."
                    />

                    <Alert severity="info" variant="outlined">
                        Landet als offener Punkt im Stammdaten-Editor. Dort wird die Gesprächsfrage
                        bzw. das Stichwort an der Anforderung ergänzt und die Meldung abgehakt.
                    </Alert>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={busy}>Abbrechen</Button>
                <Button variant="contained" onClick={submit} disabled={busy || !term.trim()}>
                    Melden
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AiFeedbackDialog;
